import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Users, Eye, CalendarCheck, Sparkles, ArrowRight, Loader2, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);

  const renderAnswer = (ans) => {
    if (typeof ans === 'string' && ans.startsWith('list:')) {
      const parts = ans.split('|');
      const intro = parts[0].replace('list:', '');
      const items = parts.slice(1);
      return (
        <div>
          <p className="mb-3">{intro}</p>
          <ul className="list-disc list-inside ml-2 space-y-1">
            {items.map((item, i) => <li key={i}>{item}</li>)}
          </ul>
        </div>
      );
    }
    return ans;
  };

  return (
    <div className="border-b border-[#E8E8E4]">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left"
        data-testid={`faq-${question.slice(0, 20).replace(/\s/g, '-').toLowerCase()}`}
      >
        <span className="font-medium text-[#1A1A1A] text-lg pr-4">{question}</span>
        <ChevronDown className={`w-5 h-5 text-[#C4A77D] flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="pb-5 text-[#595959] leading-relaxed"
        >
          {renderAnswer(answer)}
        </motion.div>
      )}
    </div>
  );
}

export default function PartnerPage() {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    company: '',
    contact: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/partner/apply`, {
        company: formData.company,
        contact: formData.contact,
        email: formData.email,
        phone: formData.phone,
        message: formData.message
      });
      toast.success(t('partner.successMsg'));
      setFormData({ company: '', contact: '', email: '', phone: '', message: '' });
    } catch (err) {
      toast.error(t('partner.errorMsg') || 'Något gick fel');
    } finally {
      setLoading(false);
    }
  };

  const faqs = t('partner.faqs') || [];

  const benefits = [
    { icon: Users, titleKey: 'partner.benefit1', descKey: 'partner.benefit1Desc', image: '/partner-tjej.png' },
    { icon: Eye, titleKey: 'partner.benefit2', descKey: 'partner.benefit2Desc', image: '/partner-synlighet.png' },
    { icon: CalendarCheck, titleKey: 'partner.benefit3', descKey: 'partner.benefit3Desc', image: '/partner-bookningar.png' },
    { icon: Sparkles, titleKey: 'partner.benefit4', descKey: 'partner.benefit4Desc', image: '/partner-inspiration.png', imageStyle: { objectPosition: '60% 30%' } }
  ];

  return (
    <div className="min-h-screen bg-[#F4EDE6]" data-testid="partner-page">
      {/* Hero */}
      <section className="relative py-16 md:py-20 overflow-hidden">
        <video 
          autoPlay muted loop playsInline
          className="absolute inset-0 w-full h-full object-cover"
          src="/partner-video.mp4"
        />
        <div className="absolute inset-0 bg-black/40" />
        <div className="container-main text-center text-white relative z-10">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-['Avenir_Next'] text-4xl md:text-5xl mb-4"
          >
            {t('partner.heroTitle')}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-white/80 text-lg max-w-2xl mx-auto"
          >
            {t('partner.heroSubtitle')}
          </motion.p>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-20">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="font-['Avenir_Next'] text-3xl text-[#1A1A1A] mb-4">
              {t('partner.benefitsTitle')}
            </h2>
          </div>
          <div className="grid md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {benefits.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="relative rounded-2xl overflow-hidden h-56 group"
              >
                <img src={item.image} alt={t(item.titleKey)} className={`absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 ${item.imagePosition || 'object-center'}`} style={item.imageStyle || {}} />
                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/50 transition-colors" />
                <div className="relative z-10 h-full flex flex-col items-center justify-center text-center p-6">
                  <div className="w-14 h-14 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center">
                    <item.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-medium text-white mb-2">{t(item.titleKey)}</h3>
                  <p className="text-sm text-white/80">{t(item.descKey)}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container-main max-w-3xl mx-auto">
          <h2 className="font-['Avenir_Next'] text-3xl text-[#1A1A1A] mb-8 text-center">
            {t('partner.faqTitle')}
          </h2>
          <div data-testid="partner-faq-list">
            {faqs.map((faq, i) => (
              <FAQItem key={i} question={faq.q} answer={faq.a} />
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16 md:py-20">
        <div className="container-main max-w-xl mx-auto">
          <h2 className="font-['Avenir_Next'] text-3xl text-[#1A1A1A] mb-8 text-center">
            {t('partner.contactTitle')}
          </h2>
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 shadow-sm space-y-5" data-testid="partner-form">
            <div>
              <Label>{t('partner.company')}</Label>
              <Input
                value={formData.company}
                onChange={e => setFormData({...formData, company: e.target.value})}
                required
                className="mt-1"
                data-testid="partner-company"
              />
            </div>
            <div>
              <Label>{t('partner.contactPerson')}</Label>
              <Input
                value={formData.contact}
                onChange={e => setFormData({...formData, contact: e.target.value})}
                required
                className="mt-1"
                data-testid="partner-contact"
              />
            </div>
            <div>
              <Label>{t('partner.email')}</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={e => setFormData({...formData, email: e.target.value})}
                required
                className="mt-1"
                data-testid="partner-email"
              />
            </div>
            <div>
              <Label>{t('partner.phone')}</Label>
              <Input
                value={formData.phone}
                onChange={e => setFormData({...formData, phone: e.target.value})}
                className="mt-1"
                data-testid="partner-phone"
              />
            </div>
            <div>
              <Label>{t('partner.message')}</Label>
              <Textarea
                value={formData.message}
                onChange={e => setFormData({...formData, message: e.target.value})}
                rows={4}
                className="mt-1"
                placeholder={t('partner.messagePlaceholder')}
                data-testid="partner-message"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#C4A77D] hover:bg-[#B39669] text-white h-12 rounded-full"
              data-testid="partner-submit"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                <>{t('partner.submit')} <ArrowRight className="w-4 h-4 ml-2" /></>
              )}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
}
